import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'
import { Generator, NextlabChannelInitialisationData, NextlabChannelMessage, Provider } from '@monorepo/next-lab'
import { ChangeDetectorRef, ElementRef } from '@angular/core'
import { Content, PhaseWithContent } from '@monorepo/golab-generated'
import { MessageChannelWrapper } from './message-channel-wrapper'
import { BaseNextlabMessageHandler } from './base-nextlab-message-handler'
import { IlsModelPartsService } from '../../services'
import { NextlabActorProvider } from '../../models'

export abstract class BaseNextlabWrapperComponent {
  saveAppUrl: SafeResourceUrl
  direction = 'ltr'
  private appIframeLoaded = false
  protected nextlabMessageChannelWrapper: MessageChannelWrapper<NextlabChannelMessage> | undefined
  private nextlabMessageHandler: BaseNextlabMessageHandler | undefined

  protected constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly ilsModelPartsService: IlsModelPartsService,
    private readonly actorProvider: NextlabActorProvider,
    protected readonly nickname: string,
    protected readonly student: boolean,
  ) {
    this.saveAppUrl = this.getSanitizedAppUrl('')
  }

  private _iframe: ElementRef<HTMLIFrameElement> | undefined

  // @ViewChild('appIframe') set iframe(iframe: ElementRef<HTMLIFrameElement> | undefined) {
  //   this.setIframe(iframe)
  // }

  protected setIframe(iframe: ElementRef<HTMLIFrameElement> | undefined) {
    if (iframe) {
      this.iframeFound(iframe)
    }
  }

  protected _appContent: Content | undefined

  // @Input() set appContent(content: Content | undefined) {
  //   this.setAppContent(content)
  // }

  protected setAppContent(content: Content | undefined) {
    if (content) {
      this._appContent = content
      this._appUrl = content.url ? content.url : ''
      this.saveAppUrl = this.getSanitizedAppUrl(this._appUrl)
      this.nextlabMessageChannelWrapper = new MessageChannelWrapper<NextlabChannelMessage>()
    }
  }

  protected _appUrl = ''

  get appUrl(): string {
    return this._appUrl
  }

  iframeLoaded() {
    // console.log('iframeLoaded')
    if (this._appUrl) {
      this.appIframeLoaded = true
      this.checkIfReadyForSetupNextlabChannelMessaging()
    }
  }

  private getSanitizedAppUrl(url: string) {
    const fullAppUrl = url + this.getUrlParameters()
    // console.log('fullAppUrl', fullAppUrl)
    return this.sanitizer.bypassSecurityTrustResourceUrl(fullAppUrl)
  }

  private getUrlParameters(): string {
    const urlParameters: string[] = []
    urlParameters.push(`twenteGolab=true`)
    urlParameters.push(`expectChannel=true`)
    urlParameters.push(`peerFeedback=false`)
    urlParameters.push(`collaboration=${!this.ilsModelPartsService.published$.value}`)
    urlParameters.push(`username=${this.nickname}`)
    urlParameters.push(`lang=${this.ilsModelPartsService.language$.value}`)
    const context = this.student ? 'ils' : 'graasp'
    urlParameters.push(`context=${context}`)
    return urlParameters.length > 0 ? '?' + urlParameters.join('&') : ''
  }

  private iframeFound(iframe: ElementRef<HTMLIFrameElement>) {
    this._iframe = iframe
    this.checkIfReadyForSetupNextlabChannelMessaging()
  }

  private async checkIfReadyForSetupNextlabChannelMessaging() {
    if (this.appIframeLoaded && !!this._iframe) {
      const iframeElement = this._iframe.nativeElement
      // it is possible that the iframeElement has no contentWindow
      // this can happen when the iframe is not visible on screen
      // when the iframe comes into view, the iframeLoaded() method will be called again
      if (iframeElement?.contentWindow) {
        const initMessage = await this.getInitMessage()
        this.nextlabMessageChannelWrapper!.connect(
          iframeElement,
          initMessage,
          (port: MessagePort) => {
            this.nextlabMessageHandler = this.createNextlabMessageHandler(port, iframeElement)
            // this.nextlabMessageHandler = new NextlabMessageHandler(
            //   port,
            //   this.storageService,
            //   this.ilsModelPartsService,
            //   this.logActionService,
            //   this.sendLogActionService,
            //   this.proxyService,
            //   this.imagesService,
            //   this.reportService,
            //   iframeElement,
            // )
          },
          (message: NextlabChannelMessage) => {
            this.handleReceivedChannelMessage(message)
          },
        )
      }
    }
  }

  protected abstract createNextlabMessageHandler(
    port: MessagePort,
    iframeElement: HTMLIFrameElement,
  ): BaseNextlabMessageHandler

  private async getInitMessage(): Promise<NextlabChannelInitialisationData> {
    return {
      actor: this.actorProvider.getActor(),
      generator: this.getGenerator(),
      provider: this.getProvider(),
      ilsStructure: this.ilsModelPartsService.getIlsStructure(),
      users: await this.ilsModelPartsService.getStudents(),
      students: await this.ilsModelPartsService.getAllStudents(),
      groups: await this.ilsModelPartsService.getGroupsInfo(),
    }
  }

  private getGenerator(): Generator {
    return {
      id: this._appContent!._id,
      objectType: 'application',
      displayName: this._appContent!.title,
      url: this._appUrl,
    }
  }

  private getProvider(): Provider {
    const provider = {
      id: this.ilsModelPartsService.ilsId,
      objectType: 'ILS',
      displayName: this.ilsModelPartsService.title$.value,
      url: '',
      inquiryPhase: 'undefined',
      inquiryPhaseId: 'undefined',
      inquiryPhaseName: 'undefined',
      ilsHasAngeLA: this.ilsModelPartsService.learningAnalyticsEnabled$.value,
    }
    const currentPhase = this.getCurrentPhase()
    if (currentPhase) {
      provider.inquiryPhaseId = currentPhase._id
      provider.inquiryPhaseName = currentPhase.title
    }
    return provider
  }

  protected abstract getCurrentPhase(): PhaseWithContent | undefined

  private handleReceivedChannelMessage(channelMessage: NextlabChannelMessage) {
    this.nextlabMessageHandler!.handleMessage(channelMessage)
    this.changeDetectorRef.detectChanges()
  }
}
